/**
 * Axios Configuration with Translation Interceptor
 * 
 * Intercepts API responses and translates dynamic text
 */

import axios from 'axios';
import translationService from '../services/translationService';

// Fields that should be translated in responses
const TRANSLATABLE_FIELDS = [
  'message',
  'msg',
  'error',
  'description',
  'title',
];

/**
 * Check if a value should be translated
 */
function shouldTranslate(value) {
  return typeof value === 'string' && value.length > 0 && value.length < 500;
}

/**
 * Recursively translate translatable fields in an object
 */
async function translateObject(obj, targetLang) {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return Promise.all(obj.map(item => translateObject(item, targetLang)));
  }

  const translated = { ...obj };

  for (const key of Object.keys(translated)) {
    if (TRANSLATABLE_FIELDS.includes(key) && shouldTranslate(translated[key])) {
      try {
        translated[key] = await translationService.translateText(translated[key], targetLang);
      } catch (error) {
        console.error(`❌ [Translation] Failed to translate field "${key}":`, error);
        // Keep original value on error
      }
    } else if (typeof translated[key] === 'object' && translated[key] !== null) {
      translated[key] = await translateObject(translated[key], targetLang);
    }
  }

  return translated;
}

// Feature flag for API response translation
const ENABLE_API_RESPONSE_TRANSLATION = false; // Set to true to enable automatic API response translation

/**
 * Setup Axios interceptors for translation
 */
export function setupTranslationInterceptor() {
  // Response interceptor
  axios.interceptors.response.use(
    async (response) => {
      try {
        // Check if API response translation is enabled
        if (!ENABLE_API_RESPONSE_TRANSLATION) {
          return response;
        }

        const currentLanguage = translationService.getCurrentLanguage();
        
        console.log('🔄 [Axios Interceptor] Response received, current language:', currentLanguage);
        
        // Skip translation for French (source language)
        if (currentLanguage === 'fr' || !currentLanguage) {
          return response;
        }

        // Skip if no data
        if (!response.data) {
          return response;
        }

        // Translate response data
        console.log('🔄 [Axios Interceptor] Translating response data to', currentLanguage);
        response.data = await translateObject(response.data, currentLanguage);
        console.log('✅ [Axios Interceptor] Translation complete');
        
      } catch (error) {
        console.error('❌ [Translation] Error translating response:', error);
        // Return original response on error
      }

      return response;
    },
    async (error) => {
      // Also translate error responses
      if (error.response?.data) {
        try {
          const currentLanguage = translationService.getCurrentLanguage();
          
          if (currentLanguage && currentLanguage !== 'fr') {
            console.log('🔄 [Axios Interceptor] Translating error response to', currentLanguage);
            error.response.data = await translateObject(error.response.data, currentLanguage);
          }
        } catch (translationError) {
          console.error('❌ [Translation] Error translating error response:', translationError);
        }
      }

      return Promise.reject(error);
    }
  );

  console.log('✅ [Translation] Axios interceptor configured');
}

export default setupTranslationInterceptor;

